import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';

import { ApiError, api, unwrap } from '../api/client.js';
import { BigDoneIcon, BigInfoIcon } from '../components/icons.js';
import { Button, Card, LinkButton, Notice, TextField } from '../components/ui.js';
import { errorMessage } from '../errors.js';
import { formatDate, formatDateTime } from '../i18n.js';
import { ME_KEY, useMe } from '../session.js';
import { formatPeriodEnd, isRunning } from '../subscription.js';

/**
 * Statuses of a Stripe subscription that keep the account from being deleted (as the API checks
 * them).
 */
const BILLING_STATUSES = ['trialing', 'active', 'past_due', 'unpaid'];

/**
 * Why the API keeps the account (`409` reason, SP-A2.197, SP-A2.240): the admin team signs in
 * with it, or Stripe runs a subscription beside the row or still collects a SEPA checkout.
 */
const REFUSALS: Record<string, string> = {
  operators: 'privacy.delete.operators',
  stripe_active: 'privacy.delete.stripeActive',
  stripe_pending: 'privacy.delete.stripePending',
};

/** Opens Stripe's customer portal ("Rechnungen & Zahlungsdaten") in this tab. */
const go = (url: string) => {
  window.location.assign(url);
};

/** US-11 on the account page (account.html): "Daten exportieren" and "Konto löschen". */
export function PrivacyCards() {
  const { t, i18n } = useTranslation();
  const me = useMe();
  const client = useQueryClient();
  const navigate = useNavigate();
  const timeZone = me.data?.account.timezone;
  const billing = useQuery({ queryKey: ['billing'], queryFn: () => unwrap(api.GET('/billing')) });
  const sub = billing.data?.subscription;
  // A running Stripe subscription that renews: the API refuses the deletion, so the card says so
  // at once (audit, 10.6). A plan on invoice, free access from us or a Stripe subscription set to
  // end does not hold it up (SP-A2.231): the card says that the plan ends with the account, and
  // the own open orders with it.
  const subscribed =
    sub !== null &&
    sub !== undefined &&
    sub.provider !== 'manual' &&
    !sub.cancelAtPeriodEnd &&
    BILLING_STATUSES.includes(sub.status);
  const ending =
    sub?.provider === 'manual'
      ? sub.status === 'active'
        ? sub
        : undefined
      : sub?.cancelAtPeriodEnd && isRunning(sub)
        ? sub
        : undefined;
  const invoices = useQuery({
    queryKey: ['billing', 'invoices'],
    queryFn: () => unwrap(api.GET('/billing/invoices')),
  });
  const ordered = (invoices.data?.items ?? []).some(
    (i) => i.status === 'open' && i.method === 'bank_transfer',
  );
  const endsWith = [
    ending
      ? ending.currentPeriodEnd
        ? t('privacy.delete.planUntil', {
            date: formatPeriodEnd(
              ending.provider,
              ending.currentPeriodEnd,
              i18n.language,
              timeZone,
            ),
          })
        : t('privacy.delete.planEndless')
      : null,
    ordered ? t('privacy.delete.openOrders') : null,
  ].filter(Boolean);

  const exportData = useMutation({
    mutationFn: () => unwrap(api.POST('/account/export')),
  });

  const [confirming, setConfirming] = useState(false);
  const [password, setPassword] = useState('');
  // The password takes the focus; on second thought it goes back to the button.
  const passwordField = useRef<HTMLInputElement>(null);
  const deleteButton = useRef<HTMLButtonElement>(null);
  const [kept, setKept] = useState(false);
  useEffect(() => {
    if (confirming) passwordField.current?.focus();
    else if (kept) deleteButton.current?.focus();
  }, [confirming, kept]);
  const remove = useMutation({
    mutationFn: () => unwrap(api.DELETE('/account', { body: { password } })),
    onSuccess: (data) => {
      client.setQueryData(ME_KEY, null);
      client.clear();
      void navigate(`/account-deleted?until=${encodeURIComponent(data.deleteAt)}`, {
        replace: true,
      });
    },
  });
  // A Stripe subscription beside a plan on invoice (SP-A2.240): "Tarif & Rechnungen" shows no
  // portal for it, so the card opens it – and the portal returns here.
  const portal = useMutation({
    mutationFn: () =>
      unwrap(api.POST('/billing/portal-session', { params: { query: { returnTo: 'account' } } })),
    onSuccess: (data) => go(data.url),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    portal.reset();
    remove.mutate();
  };
  const reason =
    remove.error instanceof ApiError
      ? (remove.error.problem as { reason?: string }).reason
      : undefined;
  const conflict =
    remove.error instanceof ApiError && remove.error.code === 'CONFLICT'
      ? (reason ?? 'subscription')
      : undefined;
  const removeError =
    // The admin team signs in with the account (SP-A2.197), a subscription Stripe runs beside
    // the row (SP-A2.240), else the running subscription of the row.
    conflict
      ? t(REFUSALS[conflict] ?? 'privacy.delete.subscription')
      : remove.error instanceof ApiError && remove.error.code === 'INVALID_CREDENTIALS'
        ? t('privacy.delete.wrongPassword')
        : // Stripe could not be asked whether one runs: nothing was deleted (SP-A2.240).
          remove.error instanceof ApiError &&
            remove.error.code === 'UNAVAILABLE' &&
            reason === 'stripe_unavailable'
          ? t('privacy.delete.stripeUnavailable')
          : remove.error
            ? errorMessage(remove.error, t)
            : undefined;

  return (
    <>
      <Card>
        <h2>
          {t('privacy.export.title')} <small>{t('privacy.export.text')}</small>
        </h2>
        {exportData.isSuccess ? (
          <Notice kind="success" title={t('privacy.export.sentTitle')}>
            {t('privacy.export.sentText', {
              date: formatDateTime(exportData.data.expiresAt, i18n.language, timeZone),
            })}
          </Notice>
        ) : null}
        {exportData.isError ? (
          <Notice kind="error">
            {exportData.error instanceof ApiError && exportData.error.code === 'RATE_LIMITED'
              ? t('privacy.export.once')
              : errorMessage(exportData.error, t)}
          </Notice>
        ) : null}
        <div className="acts">
          <Button
            variant="secondary"
            busy={exportData.isPending}
            disabled={exportData.isSuccess}
            onClick={() => exportData.mutate()}
          >
            {t('privacy.export.button')}
          </Button>
        </div>
      </Card>
      <Card className="danger-zone">
        <h2>
          {t('privacy.delete.title')}{' '}
          <small>{`${t('privacy.delete.text')} ${t('privacy.delete.undo')}`}</small>
        </h2>
        {subscribed ? (
          <Notice kind="info" title={t('privacy.delete.subscriptionTitle')}>
            {t('privacy.delete.subscription')}
          </Notice>
        ) : endsWith.length > 0 ? (
          <Notice kind="info">
            {[...endsWith, t('privacy.delete.billingQuestions')].join(' ')}
          </Notice>
        ) : null}
        {confirming ? (
          <form onSubmit={submit} className="confirm">
            <div className="field-narrow">
              <TextField
                ref={passwordField}
                label={t('privacy.delete.password')}
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                error={removeError}
                required
              />
            </div>
            {portal.isError ? <Notice kind="error">{errorMessage(portal.error, t)}</Notice> : null}
            <div className="acts">
              <Button type="submit" variant="danger" busy={remove.isPending}>
                {t('privacy.delete.confirm')}
              </Button>
              {conflict === 'stripe_active' ? (
                <Button variant="secondary" busy={portal.isPending} onClick={() => portal.mutate()}>
                  {t('billing.portal')}
                </Button>
              ) : null}
              <Button
                variant="ghost"
                onClick={() => {
                  setPassword('');
                  remove.reset();
                  portal.reset();
                  setKept(true);
                  setConfirming(false);
                }}
              >
                {t('app.cancel')}
              </Button>
            </div>
          </form>
        ) : (
          <div className="acts">
            <Button
              ref={deleteButton}
              variant="danger"
              onClick={() => {
                setKept(false);
                setConfirming(true);
              }}
            >
              {t('privacy.delete.button')}
            </Button>
          </div>
        )}
      </Card>
    </>
  );
}

/** One card in the middle of the page (privacy.html .centered .card). */
function Centered({ children }: { children: ReactNode }) {
  return (
    <div className="centered">
      <div className="card">{children}</div>
    </div>
  );
}

/**
 * The title of a card that replaced the button just pressed: it takes the focus, so a screen reader
 * reads what happened instead of falling back to the top of the page.
 */
function DoneTitle({ children }: { children: string }) {
  const title = useRef<HTMLHeadingElement>(null);
  useEffect(() => title.current?.focus(), []);
  return (
    <h1 ref={title} tabIndex={-1}>
      {children}
    </h1>
  );
}

/** After the deletion (signed out): what happens next (privacy.html, state "deleted"). */
export function AccountDeleted() {
  const { t, i18n } = useTranslation();
  const [params] = useSearchParams();
  const until = params.get('until');
  return (
    <Centered>
      <BigInfoIcon />
      <h1>{t('privacy.deleted.title')}</h1>
      <p>
        {until && !Number.isNaN(Date.parse(until))
          ? t('privacy.deleted.text', { date: formatDate(until, i18n.language) })
          : t('privacy.deleted.textNoDate')}
      </p>
      <LinkButton to="/login" variant="secondary">
        {t('privacy.toLogin')}
      </LinkButton>
    </Centered>
  );
}

/** /restore-account?token=… from the deletion e-mail (privacy.html, "restore" and "restored"). */
export function RestoreAccount() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const restore = useMutation({
    mutationFn: () => unwrap(api.POST('/account/restore', { body: { token } })),
  });
  if (restore.isSuccess)
    return (
      <Centered>
        <BigDoneIcon />
        <DoneTitle>{t('privacy.restore.doneTitle')}</DoneTitle>
        {/* Within the plan (audit, 10.6; SP-A2.57): sites beyond it stay paused. */}
        <p>{t('privacy.restore.doneText')}</p>
        {/* The deletion cancelled the own open orders on invoice (SP-A2.231). */}
        {restore.data.cancelledOrders > 0 ? <p>{t('privacy.restore.ordersCancelled')}</p> : null}
        <div className="acts">
          <LinkButton to="/login">{t('privacy.toLogin')}</LinkButton>
          <LinkButton to="/billing" variant="secondary">
            {t('billing.title')}
          </LinkButton>
        </div>
      </Centered>
    );
  return (
    <Centered>
      <h1>{t('privacy.restore.title')}</h1>
      {!token ? (
        <Notice kind="error">{t('auth.verify.missingToken')}</Notice>
      ) : (
        <>
          <p>{t('privacy.restore.text')}</p>
          {restore.isError ? <Notice kind="error">{errorMessage(restore.error, t)}</Notice> : null}
          <Button size="lg" busy={restore.isPending} onClick={() => restore.mutate()}>
            {t('privacy.restore.button')}
          </Button>
        </>
      )}
    </Centered>
  );
}

/**
 * /export?token=… from the export e-mail (signed in; privacy.html, "download" and "downloaded"):
 * downloads the JSON file. Neither its size nor the link's end are known before (decision 3 of
 * SP-UX10.6).
 */
export function ExportDownload() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const url = useRef<string | null>(null);
  const download = useMutation({
    mutationFn: async () => {
      const data = await unwrap(api.POST('/account/export/download', { body: { token } }));
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      if (url.current) URL.revokeObjectURL(url.current);
      url.current = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url.current;
      // The brand's name as the API's download header has it: siteprio-export-<day>.json.
      const brand = t('app.name')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-');
      a.download = `${brand}-export-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
    },
  });
  useEffect(
    () => () => {
      if (url.current) URL.revokeObjectURL(url.current);
    },
    [],
  );
  if (download.isSuccess)
    return (
      <Centered>
        <BigDoneIcon />
        <DoneTitle>{t('privacy.download.doneTitle')}</DoneTitle>
        <LinkButton to="/account" variant="secondary">
          {t('privacy.download.toAccount')}
        </LinkButton>
      </Centered>
    );
  return (
    <Centered>
      <h1>{t('privacy.download.title')}</h1>
      {!token ? (
        <Notice kind="error">{t('auth.verify.missingToken')}</Notice>
      ) : (
        <>
          <p>{t('privacy.download.text')}</p>
          {download.isError ? (
            <Notice kind="error">{errorMessage(download.error, t)}</Notice>
          ) : null}
          <Button size="lg" busy={download.isPending} onClick={() => download.mutate()}>
            {t('privacy.download.button')}
          </Button>
        </>
      )}
    </Centered>
  );
}
